import React from 'react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { useTranslation } from 'react-i18next';
import { Text } from '@sitecore-jss/sitecore-jss-react';

import { ContentRichText } from './../ContentRichText';
import { DaysAndTimesProps } from './definitions';
import { formatDays } from './helpers';

import {
  Card,
  Group,
  Heading,
  Description,
  Footer,
  DayLabel,
  TimeLabel,
  DaysList,
  DaysItem,
  EditorDiv
} from './StyledDaysAndTimes';

const EditorText = compose(withEditModeNotice)(EditorDiv);
const ComponentWrapper = compose(withEditModeNotice)(Card);

const DaysAndTimes: React.FC<DaysAndTimesProps> = ({ editMode, fields }) => {
  const data = fields?.data?.datasource || null;
  const [t] = useTranslation();

  if (data === null) {
    if (editMode) {
      return (
        <EditorText
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={`This component needs a datasource to render.`}
        />
      );
    }

    return null;
  }

  if (!Boolean(data?.days && data?.days.length)) {
    if (editMode) {
      return <EditorText noticeEnabled noticeLevel="warning" noticeMessage={`No days defined.`} />;
    }

    return null;
  }

  const noonString = t('days-and-times-12-noon-value');
  const midnightString = t('days-and-times-12-midnight-value');

  return (
    <ComponentWrapper
      noticeEnabled={editMode}
      noticeLevel="info"
      noticeMessage={`To configure this component, please use the 'Edit related items' screen.`}
    >
      <Group>
        {data?.title?.value && (
          <Heading>
            <Text field={data.title} />
          </Heading>
        )}
        {data?.annotation?.value && (
          <Description>
            <ContentRichText editMode={editMode} fields={{ body: data.annotation }} />
          </Description>
        )}
        <DaysList>
          {formatDays(data.days, {
            noon: noonString,
            midnight: midnightString
          }).map((day) => {
            const times = [];

            if (day.alwaysOpen === false) {
              day.times.forEach((time) => {
                times.push(
                  <TimeLabel key={time.id}>
                    {time.open} {t('days-and-times-to-conjunction')} {time.close}
                  </TimeLabel>
                );
              });

              if (times.length === 0) {
                times.push(<TimeLabel key="closed">{t('days-and-times-closed-value')}</TimeLabel>);
              }
            } else {
              times.push(<TimeLabel key="open">{t('days-and-times-open-24-value')}</TimeLabel>);
            }

            let names;

            switch (day.name.length) {
              case 1:
                names = day.name[0];
                break;
              case 2:
                names = day.name.join(` ${t('days-and-times-and-conjunction')} `);
                break;

              // 3 or more
              default:
                names = `${day.name[0]} ${t('days-and-times-to-conjunction')} ${
                  day.name[day.name.length - 1]
                }`;
                break;
            }

            return (
              <DaysItem key={day.id}>
                <DayLabel>{names}</DayLabel>
                {times}
              </DaysItem>
            );
          })}
        </DaysList>
        {data?.footer?.value && (
          <Footer>
            <ContentRichText editMode={editMode} fields={{ body: data.footer }} />
          </Footer>
        )}
      </Group>
    </ComponentWrapper>
  );
};

export default compose(withEditMode)(DaysAndTimes);
